import React from 'react'
import styled from 'styled-components'
import { useStateMachine } from 'little-state-machine'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Store } from '../Scale'

import colors from '../../../styles/colors'
import { InquiryFormType } from '../../../utils/constants'

type Props = {
  answers: string[]
  questions: string[]
  title: string
  formType: InquiryFormType['name']
  allMatrixQuestionsByScaleType: string[]
}
export function Matrix({
  answers,
  questions,
  title,
  formType,
  allMatrixQuestionsByScaleType,
}: Props) {
  const findQuestionIndex = (question: string) =>
    allMatrixQuestionsByScaleType.indexOf(question) + 1
  const { t } = useTranslation('translation')
  return (
    <React.Fragment>
      <div>
        <Question>
          <h2>{t(`${formType}.title.${title}`)}</h2>
        </Question>
        <Answers aria-hidden>
          {[...answers].reverse().map((answer) => (
            <Answer key={answer}>
              {!formType.includes('KAPP_S_') && (
                <PositionFixingDiv>
                  <Tooltip>
                    <strong>{t(`answers.${answer}`)}</strong> –{' '}
                    {t(`answers.tooltip.${answer}`)}
                  </Tooltip>
                </PositionFixingDiv>
              )}
              {t(`answers.${answer}`)}
            </Answer>
          ))}
        </Answers>
      </div>
      {questions.map((question) => (
        <Row key={question}>
          <Question>
            <No>{findQuestionIndex(question)}</No>
            <div>
              {t(`${formType}.questions.${question}`)
                .split('$i$')
                .map((split, index) =>
                  index === 0 || index % 2 === 0 ? split : <i>{split}</i>
                )}
            </div>
          </Question>
          <RadioGroup name={question} options={answers} />
        </Row>
      ))}
    </React.Fragment>
  )
}

type RadioProps = {
  name: string
  options: string[]
}
export function RadioGroup({ name, options }: RadioProps) {
  const { register, setValue } = useFormContext()
  const { t } = useTranslation('translation')

  const {
    state: { inquery },
  } = useStateMachine<Store>()

  React.useEffect(() => {
    setValue(name, inquery.defaultValues[name])
  }, [inquery.defaultValues])

  return (
    <Group role="radiogroup">
      {options.map((option, index) => (
        <input
          key={`${name}-${option}`}
          name={name}
          type="radio"
          aria-description={t(`answers.${option}`)}
          value={index + 1}
          ref={register({ required: true })}
        />
      ))}
    </Group>
  )
}

const Answers = styled.div`
  display: flex;
  flex-direction: row-reverse;
`
const PositionFixingDiv = styled.div`
  position: relative;
`

const Answer = styled.div`
  /* @todo add highlight when hover */
  width: 4rem;
  padding: 0;
  font-size: 0.75rem;
  display: flex;
  justify-content: center;
  text-align: center;
  align-items: flex-end;
`

const No = styled.output`
  background: var(--color-primary);
  color: var(--color-text-button);
  display: flex;
  height: 3rem;
  width: 3rem;
  border-radius: 1rem;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  margin: 0 0.5em;
`

const Question = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1 0;
  align-items: center;
  > span {
    display: flex;
    align-items: center;
  }
`

const Row = styled.div`
  overflow: hidden;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  height: 100%;
  background: var(--color-background-light);
  &:nth-of-type(odd) {
    background: var(--color-background-dark);
  }
  &:nth-of-type(2) {
    border-radius: 1em 1em 0 0;
  }
  &:last-of-type {
    border-radius: 0 0 1em 1em;
  }
`

const Group = styled.div`
  display: flex;
  input {
    width: 4rem;
    height: 1.3rem;
  }
  > label {
    padding: 1rem 0;
  }
`

const Tooltip = styled.span`
   
    width: 10rem;
    visibility: hidden;
    background-color: ${colors.red};
    color: #fff;
    right: 5%;
    text-align: center;
    border-radius: 6px;
    position: absolute;
    opacity: 0;
    transition: opacity 0.3s;
  }
  ${Answer}:hover & {
    visibility: visible;
    opacity: 1;
  
`
/* 
// @todo make it in ozp colour :D
// const RadioButton = styled.input`` */
